import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { getSector, SECTORS } from "../lib/sectors";

export default function SectorDetailPage() {
  const { slug } = useParams();
  const sector = getSector(slug);
  if (!sector) return <Navigate to="/" replace />;

  const Icon = sector.Icon;
  const others = SECTORS.filter((s) => s.slug !== slug).slice(0, 4);

  return (
    <main data-testid={`sector-page-${slug}`} className="bg-canvas">
      {/* Hero */}
      <section className="relative bg-navy text-ivory pt-32 pb-24 md:pt-40 md:pb-36 overflow-hidden grain">
        <div className="absolute inset-0">
          <img
            src={sector.image}
            alt={sector.name}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/85 via-navy/75 to-navy" />
        </div>
        <div
          className="absolute inset-0 bg-grid-faint opacity-20"
          style={{ backgroundSize: "80px 80px" }}
        />

        <div className="relative container-bharat">
          <Link
            to="/#sectors"
            data-testid="sector-back-link"
            className="inline-flex items-center gap-2 overline text-gold hover:text-ivory transition-colors"
          >
            <ArrowLeft size={14} /> Back to Sectors
          </Link>

          <div className="mt-10 flex items-center gap-3">
            <span className="w-12 h-12 inline-flex items-center justify-center border border-gold/40 text-gold">
              <Icon size={20} />
            </span>
            <span className="overline text-gold">Practice</span>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif-display text-5xl sm:text-6xl lg:text-7xl mt-8 max-w-4xl leading-[0.98] tracking-tight"
          >
            {sector.name}.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-8 max-w-2xl text-lg text-ivory/75 italic font-serif-display"
          >
            {sector.tagline}
          </motion.p>
        </div>
      </section>

      {/* Intro + Metrics */}
      <section className="bg-ivory py-24 md:py-32">
        <div className="container-bharat grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="overline">Practice Overview</p>
            <p className="font-serif-display text-3xl md:text-4xl text-navy mt-6 leading-[1.15] max-w-2xl">
              {sector.intro}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-3 gap-px bg-graphite/10">
              {sector.metrics.map((m) => (
                <div
                  key={m.label}
                  className="bg-ivory p-6"
                  data-testid={`sector-metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <p className="font-serif-display text-3xl text-navy">
                    {m.value}
                  </p>
                  <p className="text-[0.7rem] text-graphite/60 mt-2 leading-snug">
                    {m.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-canvas py-24 md:py-32">
        <div className="container-bharat">
          <div className="max-w-3xl">
            <p className="overline">Capabilities</p>
            <h2 className="font-serif-display text-3xl md:text-5xl text-navy mt-6 leading-[1.05]">
              How we partner with{" "}
              <span className="italic text-gold">institutions</span>.
            </h2>
          </div>
          <div className="hairline my-12" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-graphite/10">
            {sector.capabilities.map((c, i) => (
              <motion.div
                key={c}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                className="bg-canvas p-8 flex items-start gap-5 hover:bg-white transition-colors"
              >
                <CheckCircle2 className="text-gold mt-1 shrink-0" size={18} />
                <div>
                  <span className="overline text-graphite/40 text-[0.6rem]">
                    Capability · 0{i + 1}
                  </span>
                  <p className="mt-2 text-navy text-lg font-serif-display leading-snug">
                    {c}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-navy text-ivory py-20 md:py-28">
        <div className="container-bharat flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <div>
            <p className="overline text-gold">Engage</p>
            <h3 className="font-serif-display text-3xl md:text-5xl mt-5 max-w-2xl leading-[1.08]">
              Discuss a {sector.short.toLowerCase()} mandate with our partners.
            </h3>
          </div>
          <Link
            to="/#engage"
            data-testid="sector-cta-engage"
            className="inline-flex items-center gap-3 px-7 py-4 bg-gold text-navy text-sm tracking-wide hover:bg-ivory transition-all duration-300"
          >
            Submit Inquiry
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      {/* Other sectors */}
      <section className="bg-ivory py-24 md:py-32">
        <div className="container-bharat">
          <p className="overline">Other Practices</p>
          <div className="hairline my-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {others.map((o) => {
              const OIcon = o.Icon;
              return (
                <Link
                  key={o.slug}
                  to={`/sectors/${o.slug}`}
                  data-testid={`other-sector-${o.slug}`}
                  className="group block border border-graphite/10 hover:border-navy bg-canvas hover:bg-white p-7 transition-all duration-500"
                >
                  <OIcon size={20} className="text-gold" />
                  <h4 className="font-serif-display text-xl md:text-2xl text-navy mt-6 leading-tight">
                    {o.name}
                  </h4>
                  <p className="mt-3 text-xs text-graphite/60 leading-relaxed">
                    {o.tagline}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs text-navy underline-grow">
                    Explore <ArrowUpRight size={12} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}
